/** @jsxImportSource @emotion/react */
import { useEffect, useState } from "react";
import * as authActions from "../auth/authActions";
import * as authSelectors from "../auth/authSelectors";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { RootState } from "../../store";
import { SpinnerBlock } from "./Spinner";
import { routePatterns } from "../../appRoutingHelpers";
import { ErrorBoundaryComponent } from "../../components/ErrorBoundaryComponent";

export const AppProtectedRoutes = () => {
  const isAuthenticated = useSelector(authSelectors.isUserAuthenticated);
  const { organization } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const isSelectingOrganization = useSelector(
    authSelectors.isSelectingOrganization
  );
  const isAuthenticatedForOrganization = useSelector((state: RootState) =>
    authSelectors.isAuthenticatedForChosenOrganization(
      state,
      organization || ""
    )
  );

  useEffect(() => {
    // If user is not authenticated, redirect to login
    if (!isAuthenticated || !isAuthenticatedForOrganization) {
      setIsLoading(false);
      navigate(routePatterns.login);
      return;
    }

    const selectOrganization = (organization: string) => {
      dispatch(authActions.selectOrganizationAction(organization));
    };

    // If user is authenticated for org, and org is not selected, select it
    if (!organizationKeyName && organization && !isSelectingOrganization) {
      selectOrganization(organization);
      return;
    }

    // Wait for organization to be selected
    if (isSelectingOrganization) {
      return;
    }

    // Render UI
    setIsLoading(false);
  }, [
    isAuthenticatedForOrganization,
    isAuthenticated,
    organizationKeyName,
    organization,
    isSelectingOrganization,
    navigate,
    dispatch,
  ]);

  if (isLoading) {
    return <SpinnerBlock />;
  }

  return (
    <ErrorBoundaryComponent>
      <Outlet />
    </ErrorBoundaryComponent>
  );
};
